import React, { useState, useEffect, useMemo } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../../../graphql/iot-documents';
import {
  formatPowerAutoUnit,
  formatValue,
  createReconnectingSubscription,
} from '../../../../utils';
import * as s from './styled-machine-comparison';

// Comparacao por maquina (thing) da usina: barra horizontal proporcional
// da potencia atual de cada maquina + vazao. Responde de relance "qual
// UCHA/bomba esta puxando o resultado" sem abrir cada uma.
//
// Autocontido: assina onUpdateIOT por thing (mesmo padrao do Resumo) em vez
// de depender do redux state.aplicacao.realtimeData — que hoje guarda so a
// ULTIMA thing (o reducer substitui em vez de mesclar). Isolar aqui evita
// regressao na tela operacional que le aquele state.

// UCHA gera com PotenciaAtivaMedia negativo (convencao do grid). Exibimos o
// modulo positivo, igual ao resto do CCO.
const powerMagnitude = (v) => Math.abs(Number(v) || 0);

const MachineComparison = ({ things = [], type }) => {
  const [dataByThing, setDataByThing] = useState({});
  const accent = type === 'eba' ? 'eba' : 'ucha';

  useEffect(() => {
    let mounted = true;
    const recons = [];
    setDataByThing({});

    Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
      ([iotQuery, updateIotSubscription]) => {
        if (!mounted) return;
        things.forEach(({ thing }) => {
          const apply = (iot) => {
            if (!iot || !mounted) return;
            setDataByThing((prev) => ({
              ...prev,
              [thing]: {
                power: iot.PotenciaAtivaMedia,
                flow: iot.Vazao,
              },
            }));
          };
          // Snapshot inicial
          API.graphql(graphqlOperation(iotQuery, { thingName: thing }))
            .then((res) =>
              apply('getIOT' in res ? res.getIOT : res.data?.getIOT)
            )
            .catch(() => {});
          // Subscription com reconnect
          const recon = createReconnectingSubscription({
            label: `machine-comparison/${thing}`,
            buildSubscription: ({ next, error }) =>
              API.graphql(
                graphqlOperation(updateIotSubscription, { thingName: thing })
              ).subscribe({ next, error }),
            onNext: ({ value }) =>
              apply(value && value.data && value.data.onUpdateIOT),
          });
          recons.push(recon);
        });
      }
    );

    return () => {
      mounted = false;
      recons.forEach((r) => {
        if (r && typeof r.unsubscribe === 'function') r.unsubscribe();
      });
    };
  }, [things]);

  // Linhas ordenadas por potencia desc (maior contribuinte no topo).
  const rows = useMemo(() => {
    const list = things.map((th, idx) => {
      const d = dataByThing[th.thing] || {};
      return {
        key: th.thing || `m-${idx}`,
        name: th.name || th.thing || `Máquina ${idx + 1}`,
        power: powerMagnitude(d.power),
        flow: Number.isFinite(Number(d.flow)) ? Number(d.flow) : null,
        hasData: d.power != null,
      };
    });
    return list.sort((a, b) => b.power - a.power);
  }, [things, dataByThing]);

  const maxPower = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.power), 0),
    [rows]
  );

  // So faz sentido comparar com 2+ maquinas.
  if (things.length < 2) return null;

  return (
    <s.List>
      {rows.map((r) => {
        const pct = maxPower > 0 ? (r.power / maxPower) * 100 : 0;
        return (
          <s.Row key={r.key}>
            <s.RowHead>
              <s.Name title={r.name}>{r.name}</s.Name>
              <s.Value>
                {r.hasData ? formatPowerAutoUnit(r.power, 'kW') : '—'}
                {r.flow != null && r.flow !== 0 && (
                  <s.Flow>{formatValue(r.flow)} l/s</s.Flow>
                )}
              </s.Value>
            </s.RowHead>
            <s.BarTrack>
              <s.BarFill $accent={accent} style={{ width: `${pct}%` }} />
            </s.BarTrack>
          </s.Row>
        );
      })}
    </s.List>
  );
};

export default MachineComparison;
